import { DAY_MS } from '../../constants';
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Target, AlertTriangle, Sparkles, CheckCircle2 } from 'lucide-react';
import { computeLaneScore } from '../../lib/analytics';
import { getChampionIconUrl, getItemIconUrl, getRuneIconUrl, findBootsInBuild } from '../../lib/champions';
import { getGameContributions } from '../../lib/objectives';
import { analyzeGame } from '../../lib/gameInsights/index';

/**
 * APEX v7.2 — Game History Card refondu
 *
 * Nouveautés :
 *   - Bandeau couleur épais sur toute la card (W = cyan, L = rose)
 *   - Badge "WON LANE / DRAWN LANE / LOST LANE" visible (pas juste chevron)
 *   - Items finaux + runes affichés dans l'expand (si game.items / game.runes présents)
 *
 * Schema attendu :
 *   - champion, support, enemyAdc, enemySupport, jungler, enemyJungler
 *   - result, duration (secondes), kills, deaths, assists, csPerMin
 *   - timeFrames (pour stats à 15 min)
 *   - deathDetails (greed/position/investment)
 *   - mood, notes, mySide, patch
 *   - items: [id, id, id, id, id, id, trinketId]  (v7.2)
 *   - runes: { primaryStyle, secondaryStyle, keystone, primary, secondary, shards }  (v7.2)
 */

/**
 * Build compact format : grille 2×3 items + carré quête (trinket) + rond keystone
 * Utilisé dans la card repliée pour avoir un aperçu visuel sans expand.
 *
 *   ┌────┬────┬────┐  ┌────┐  ┌────┐
 *   │ i1 │ i2 │ i3 │  │ Q  │  │ K  │
 *   ├────┼────┼────┤  │    │  │    │
 *   │ i4 │ i5 │ i6 │  └────┘  └────┘
 *   └────┴────┴────┘
 *
 * Si game.items / game.runes ne sont pas dispo (game pré-v7.2), affiche rien.
 */
function BuildCompact({ items, runes, version }) {
  const hasItems = Array.isArray(items) && items.some(id => id && id !== 0);
  const hasRune = runes?.keystone;

  if (!hasItems && !hasRune) return null;

  // V7.2.4 : la "quête" pour un ADC = les bottes upgradées (T2 ou T3)
  // On les détecte dynamiquement dans tous les slots via la liste des Boots IDs.
  const bootsId = findBootsInBuild(items);

  // Pour la grille 2×3 : on prend les 6 premiers items, MAIS on retire les bottes
  // pour éviter le doublon avec le carré Q. Si trinket (slot 6) est dans la grille,
  // on l'exclut aussi.
  const buildItems = Array.isArray(items)
    ? items
        .filter((id, idx) => {
          if (!id || id === 0) return true; // garde slots vides pour conserver la grille
          if (id === bootsId) return false; // les bottes vont dans le carré Q
          return true;
        })
        .slice(0, 6)
    : [0, 0, 0, 0, 0, 0];
  // Padding pour toujours afficher 6 cases
  while (buildItems.length < 6) buildItems.push(0);

  const ItemSquare = ({ itemId, size = 22 }) => (
    <div
      className="border border-zinc-800 bg-zinc-900 overflow-hidden shrink-0"
      style={{ width: size, height: size }}
    >
      {itemId && itemId !== 0 ? (
        <img
          src={getItemIconUrl(itemId, version)}
          alt={`Item ${itemId}`}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : null}
    </div>
  );

  return (
    <div className="flex items-center gap-2 shrink-0">
      {/* Grille 2×3 des items (bottes exclues) */}
      {hasItems && (
        <div className="grid grid-cols-3 gap-[2px]">
          {buildItems.map((id, i) => (
            <ItemSquare key={i} itemId={id} size={20} />
          ))}
        </div>
      )}

      {/* Carré quête : bottes (Role Quest slot ADC ou slot classique) */}
      {bootsId && (
        <div
          className="border border-cyan-400/30 bg-zinc-900 overflow-hidden shrink-0"
          style={{ width: 42, height: 42 }}
          title="Bottes (Role Quest ADC)"
        >
          <img
            src={getItemIconUrl(bootsId, version)}
            alt={`Boots ${bootsId}`}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}

      {/* Rond keystone */}
      {hasRune && (
        <div
          className="rounded-full border-2 border-amber-400/50 bg-zinc-900 overflow-hidden shrink-0"
          style={{ width: 42, height: 42 }}
          title="Keystone"
        >
          <img
            src={getRuneIconUrl(runes.keystone)}
            alt="Keystone"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
}

function LaneBadge({ game, role = 'ADC', compact = false }) {
  const lane = computeLaneScore(game, 15, role);
  if (!lane) {
    return compact ? (
      <span className="text-zinc-700 text-[10px]" title="Lane data indisponible">—</span>
    ) : null;
  }
  const config = {
    won:   { color: 'cyan',  label: 'WON LANE',   chev: '↗', score: lane.score },
    drawn: { color: 'amber', label: 'DRAWN LANE', chev: '→', score: lane.score },
    lost:  { color: 'rose',  label: 'LOST LANE',  chev: '↘', score: lane.score }
  };
  const c = config[lane.status];
  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border border-${c.color}-400/40 bg-${c.color}-400/10 text-${c.color}-400 text-[9px] font-bold tracking-wider font-mono`}
      title={`Score lane à 15min: ${lane.score}`}
    >
      <span className="text-[11px]">{c.chev}</span>
      <span>{c.label}</span>
      <span className="text-[9px] opacity-70">{lane.score > 0 ? `+${lane.score}` : lane.score}</span>
    </div>
  );
}

function formatRelativeTime(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const days = Math.floor(diff / DAY_MS);
  if (hours < 1) return "à l'instant";
  if (hours < 24) return `il y a ${hours}h`;
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days}j`;
  return new Date(timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function MatchupRow({ label, mine, enemy, version }) {
  if (!mine && !enemy) return null;
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className="text-zinc-600 font-mono w-10 shrink-0">{label}</span>
      <div className="flex items-center gap-1 flex-1 min-w-0">
        {mine && (
          <>
            <img
              src={getChampionIconUrl(mine, version)}
              alt={mine}
              className="w-5 h-5 object-cover shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="text-zinc-300 font-mono truncate">{mine}</span>
          </>
        )}
      </div>
      <span className="text-zinc-600 font-mono shrink-0">vs</span>
      <div className="flex items-center gap-1 flex-1 min-w-0 justify-end">
        {enemy && (
          <>
            <span className="text-zinc-400 font-mono truncate">{enemy}</span>
            <img
              src={getChampionIconUrl(enemy, version)}
              alt={enemy}
              className="w-5 h-5 object-cover shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ItemsRow({ items, version }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const hasAny = items.some(id => id && id !== 0);
  if (!hasAny) return null;

  // 6 slots build + 1 trinket
  const buildItems = items.slice(0, 6);
  const trinket = items[6];

  return (
    <div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-1.5">
        Build final
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {buildItems.map((itemId, idx) => (
          <div
            key={idx}
            className="w-7 h-7 border border-zinc-800 bg-zinc-900 flex items-center justify-center overflow-hidden"
          >
            {itemId && itemId !== 0 ? (
              <img
                src={getItemIconUrl(itemId, version)}
                alt={`Item ${itemId}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <span className="text-zinc-700 text-[10px]">—</span>
            )}
          </div>
        ))}
        {trinket && trinket !== 0 && (
          <>
            <span className="text-zinc-700 mx-1 text-xs">·</span>
            <div className="w-7 h-7 border border-cyan-400/30 bg-zinc-900 overflow-hidden" title="Trinket">
              <img
                src={getItemIconUrl(trinket, version)}
                alt={`Trinket ${trinket}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RunesRow({ runes, version }) {
  if (!runes || (!runes.keystone && !runes.secondaryStyle)) return null;
  return (
    <div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-1.5">
        Runes
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Primaire (keystone) */}
        {runes.keystone && (
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 border border-amber-400/40 bg-zinc-900 overflow-hidden" title="Keystone">
              <img
                src={getRuneIconUrl(runes.keystone, version)}
                alt="Keystone"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            {Array.isArray(runes.primary) && runes.primary.slice(1, 4).map((id, idx) => (
              <div
                key={idx}
                className="w-5 h-5 border border-zinc-800 bg-zinc-900 overflow-hidden"
              >
                <img
                  src={getRuneIconUrl(id, version)}
                  alt={`Rune ${id}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Séparateur visuel */}
        {runes.secondaryStyle && <span className="text-zinc-700 text-xs">·</span>}

        {/* Secondaire (2 runes) */}
        {Array.isArray(runes.secondary) && runes.secondary.length > 0 && (
          <div className="flex items-center gap-1.5">
            {runes.secondary.slice(0, 2).map((id, idx) => (
              <div
                key={idx}
                className="w-5 h-5 border border-zinc-800 bg-zinc-900 overflow-hidden"
              >
                <img
                  src={getRuneIconUrl(id, version)}
                  alt={`Rune ${id}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeathBreakdown({ deathDetails }) {
  if (!Array.isArray(deathDetails) || deathDetails.length === 0) return null;
  const counts = deathDetails.reduce((acc, d) => {
    const t = d.type || 'uncategorized';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const conf = [
    { key: 'greed',      label: 'Greed',      cls: 'text-rose-400 border-rose-400/30 bg-rose-400/5' },
    { key: 'position',   label: 'Position',   cls: 'text-amber-400 border-amber-400/30 bg-amber-400/5' },
    { key: 'investment', label: 'Investment', cls: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/5' }
  ];
  const visible = conf.filter(c => counts[c.key] > 0);
  if (visible.length === 0 && !counts.uncategorized) return null;
  return (
    <div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-1.5">
        Morts ({deathDetails.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map(c => (
          <span key={c.key} className={`text-[10px] font-mono font-bold border px-1.5 py-0.5 ${c.cls}`}>
            {counts[c.key]} {c.label}
          </span>
        ))}
        {counts.uncategorized > 0 && (
          <span className="text-[10px] font-mono text-zinc-500 border border-zinc-800 px-1.5 py-0.5">
            {counts.uncategorized} ?
          </span>
        )}
      </div>
    </div>
  );
}

function LaneStatsAt15({ game, role = 'ADC' }) {
  const tf = game?.timeFrames?.min15;
  if (!tf?.my || !tf?.opp) return null;
  const goldDiff = (tf.my.gold || 0) - (tf.opp.gold || 0);
  const xpDiff   = (tf.my.xp   || 0) - (tf.opp.xp   || 0);
  const csDiff   = (tf.my.cs   || 0) - (tf.opp.cs   || 0);
  const fmt = (n) => `${n > 0 ? '+' : ''}${n}`;
  const cls = (n) => n > 0 ? 'text-cyan-400' : n < 0 ? 'text-rose-400' : 'text-zinc-500';

  // Adversaire de lane selon le rôle
  const ROLE_OPP = {
    ADC:    game.enemyAdc,
    MID:    game.enemyMid,
    TOP:    game.enemyTop,
    JUNGLE: game.enemyJungler,
    SUPPORT: game.enemySupport,
  };
  const oppName = ROLE_OPP[role] || game.enemyAdc;
  const showCs = role !== 'JUNGLE'; // Jungle n'a pas de CS lane

  return (
    <div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-1.5">
        Diff @15min {oppName ? `vs ${oppName}` : 'vs adversaire'}
      </div>
      <div className={`grid gap-2 text-[10px] font-mono ${showCs ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <div className="border border-zinc-800 px-2 py-1.5">
          <div className="text-zinc-500 text-[9px]">Gold</div>
          <div className={`font-bold ${cls(goldDiff)}`}>{fmt(goldDiff)}</div>
        </div>
        <div className="border border-zinc-800 px-2 py-1.5">
          <div className="text-zinc-500 text-[9px]">XP</div>
          <div className={`font-bold ${cls(xpDiff)}`}>{fmt(xpDiff)}</div>
        </div>
        {showCs && (
          <div className="border border-zinc-800 px-2 py-1.5">
            <div className="text-zinc-500 text-[9px]">CS</div>
            <div className={`font-bold ${cls(csDiff)}`}>{fmt(csDiff)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GameHistoryCard({
  game,
  version,
  defaultExpanded = false,
  lpDelta = null,
  onAnalyze,
  miniBlocks = [],
  userStats = null,
  userSettings = null
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // V7.7.1 : contributions aux objectifs actifs (calculé seulement à l'expand)
  const contributions = useMemo(() => {
    if (!expanded) return [];
    return getGameContributions(game, miniBlocks);
  }, [expanded, game, miniBlocks]);

  // V7.7.1 : insights game-level (catégorie A morts contextualisées)
  const insights = useMemo(() => {
    if (!expanded) return [];
    return analyzeGame(game, { userStats });
  }, [expanded, game, userStats]);
  const isWin = game.result === 'W';
  // v8.5 : durée précise depuis durationSeconds (mm:ss), fallback sur duration (minutes)
  const durationDisplay = (() => {
    if (game.durationSeconds > 0) {
      const m = Math.floor(game.durationSeconds / 60);
      const s = game.durationSeconds % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    }
    const m = Math.round((game.duration || 0) / 60);
    return m > 0 ? `${m}min` : null;
  })();
  // v8.5 : heure de début depuis startTime (UNIX ms)
  const startTimeDisplay = (() => {
    const ts = game.startTime || game.gameStartTimestamp;
    if (!ts) return null;
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  })();
  const duration = Math.round((game.duration || 0) / 60);

  // V7.5.1 : détection game annulée (flag Riot officiel)
  // gameEndedInEarlySurrender = remake auto Riot < 3min30 ratifié par l'équipe
  // v7.7.9 FIX : duration est en MINUTES, seuil annulation = < 5 min (pas 300s)
  const isCancelled = game.isRemake === true
    || (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5);

  // v8.22 : badge rôle basé sur primary_role / secondary_role de userSettings
  // Normaliser game.role ('ADC','MID','TOP','JG','SUP') vers les rôles complets
  // pour comparaison avec primary_role / secondary_role de userSettings
  const GAME_ROLE_TO_FULL = {
    ADC: 'ADC', MID: 'MID', TOP: 'TOP',
    JG: 'JUNGLE', SUP: 'SUPPORT',
    JUNGLE: 'JUNGLE', SUPPORT: 'SUPPORT', // si déjà complet
  };
  const rawRole = game.role || (game.isAutofill ? null : 'ADC');
  const playedRole = GAME_ROLE_TO_FULL[rawRole] || 'ADC';
  const primaryRole   = userSettings?.primary_role   || 'ADC';
  const secondaryRole = userSettings?.secondary_role || null;

  const roleBadge = (() => {
    if (playedRole === primaryRole) return null; // rôle principal = pas de badge
    if (secondaryRole && playedRole === secondaryRole) return 'SECONDAIRE';
    return 'AUTOFILL';
  })();
  const isAutofill = roleBadge === 'AUTOFILL'; // conservé pour compatibilité

  // V7.2 : Bandeau couleur épais + tint subtil du background
  // V7.5.1 : si annulée, bandeau et tint en gris (l'info W/L n'est pas significative)
  let wlBandClass, wlTintClass, wlBorderClass;
  if (isCancelled) {
    wlBandClass = 'bg-zinc-700';
    wlTintClass = 'bg-zinc-900/[0.4]';
    wlBorderClass = 'border-zinc-800 hover:border-zinc-700';
  } else {
    wlBandClass = isWin ? 'bg-cyan-400' : 'bg-rose-400';
    wlTintClass = isWin ? 'bg-cyan-400/[0.04]' : 'bg-rose-400/[0.04]';
    wlBorderClass = isWin ? 'border-cyan-400/20 hover:border-cyan-400/40' : 'border-rose-400/20 hover:border-rose-400/40';
  }

  // V7.5.1 : opacity réduite sur la card si annulée
  const cardOpacity = isCancelled ? 'opacity-60' : '';

  const handleToggle = () => setExpanded(prev => !prev);

  return (
    <div
      className={`
        flex border ${wlBorderClass}
        ${wlTintClass} ${cardOpacity}
        transition cursor-pointer relative overflow-hidden
      `}
      onClick={handleToggle}
    >
      {/* V7.2.3 : Bandeau couleur épaissi (compense la suppression de la lettre W/L) */}
      <div className={`w-2 ${wlBandClass} shrink-0`} />

      <div className="flex-1 min-w-0">
        {/* Header (toujours visible) */}
        <div className="flex items-center gap-3 px-3 py-3">
          {/* V7.2.3 : W/L lettre retirée — le bandeau coloré à gauche suffit */}

          {/* Champion icon + matchup */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {game.champion && (
              <img
                src={getChampionIconUrl(game.champion, version)}
                alt={game.champion}
                className="w-9 h-9 object-cover shrink-0 border border-zinc-800"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-white truncate flex items-center gap-2 flex-wrap">
                {game.champion}
                {/* V7.5.1 : Badge "Annulée" si game <10min */}
                {isCancelled && (
                  <span className="inline-flex items-center px-1.5 py-0.5 border border-zinc-700 bg-zinc-900 text-zinc-400 text-[9px] font-bold tracking-wider font-mono">
                    ANNULÉE
                  </span>
                )}
                {/* v8.22 : Badge SECONDAIRE ou AUTOFILL selon rôle joué vs rôles configurés */}
                {roleBadge && !isCancelled && (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 border text-[9px] font-bold tracking-wider font-mono ${
                      roleBadge === 'SECONDAIRE'
                        ? 'border-violet-400/40 bg-violet-400/10 text-violet-400'
                        : 'border-amber-400/40 bg-amber-400/10 text-amber-400'
                    }`}
                    title={`Joué en ${playedRole} — rôle ${roleBadge === 'SECONDAIRE' ? 'secondaire' : 'non configuré'}`}
                  >
                    {roleBadge}
                  </span>
                )}
                {!isCancelled && <LaneBadge game={game} role={playedRole} />}
              </div>
              <div className="text-[10px] text-zinc-500 font-mono truncate flex items-center gap-2">
                {/* v8.22 : adversaire de lane selon le rôle joué */}
                {(() => {
                  const enemy = {
                    ADC:    game.enemyAdc,
                    MID:    game.enemyMid,
                    TOP:    game.enemyTop,
                    JUNGLE: game.enemyJungler,
                    SUPPORT: game.enemySupport,
                  }[playedRole] || game.enemyAdc;
                  return enemy ? <span>vs {enemy}</span> : null;
                })()}
                {durationDisplay && <span>· {durationDisplay}</span>}
                {startTimeDisplay && <span>· {startTimeDisplay}</span>}
                {game.queue_type && <span>· {game.queue_type}</span>}
                {game.patch && (
                  <span className="inline-flex items-center px-1.5 py-0.5 border border-zinc-800 bg-zinc-900/60 text-zinc-400 text-[9px] font-bold tracking-wider">
                    v{game.patch}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* KDA */}
          <div className="flex flex-col items-end shrink-0">
            <div className="font-mono text-sm font-bold whitespace-nowrap">
              <span className="text-zinc-200">{game.kills}</span>
              <span className="text-zinc-600">/</span>
              <span className="text-rose-400">{game.deaths}</span>
              <span className="text-zinc-600">/</span>
              <span className="text-zinc-400">{game.assists}</span>
            </div>
            <div className="text-[9px] text-zinc-500 font-mono">
              {game.deaths > 0
                ? `${((game.kills + game.assists) / game.deaths).toFixed(1)} KDA`
                : 'Perfect'}
            </div>
          </div>

          {/* V7.2.2 : Build compact (items 2×3 + trinket + keystone) — visible sans expand */}
          <BuildCompact items={game.items} runes={game.runes} version={version} />

          {/* LP delta + time */}
          <div className="flex flex-col items-end gap-0.5 shrink-0 min-w-[64px]">
            {lpDelta !== null && lpDelta !== undefined && (
              <div className={`font-mono text-xs font-black ${lpDelta > 0 ? 'text-cyan-400' : lpDelta < 0 ? 'text-rose-400' : 'text-zinc-500'}`}>
                {lpDelta > 0 ? '+' : ''}{lpDelta} LP
              </div>
            )}
            <div className="text-[9px] text-zinc-600 font-mono">
              {formatRelativeTime(game.timestamp)}
            </div>
          </div>

          {/* Expand chevron */}
          <button
            onClick={(e) => { e.stopPropagation(); handleToggle(); }}
            className="text-zinc-600 hover:text-zinc-400 shrink-0"
            aria-label={expanded ? "Replier" : "Déplier"}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        {/* Expanded panel */}
        {expanded && (
          <div className="border-t border-zinc-900 px-3 py-3 space-y-3 bg-zinc-950/40">
            <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500 flex-wrap">
              {game.csPerMin && <span>📊 {game.csPerMin.toFixed(1)} CS/min</span>}
              {game.mySide && (
                <span className={game.mySide === 'blue' ? 'text-cyan-400' : 'text-rose-300'}>
                  ● {game.mySide === 'blue' ? 'Blue side' : 'Red side'}
                </span>
              )}
              {game.patch && <span>v{game.patch}</span>}
            </div>

            {/* v7.8.9 : section "Contributions aux objectifs" retirée
                (peu utile dans l'analyse de game). L'analyse est enrichie
                avec plus de patterns positifs côté gameInsights. */}

            {/* V7.7.1 : Insights game-level (catégorie A) */}
            {insights.length > 0 && (
              <div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-2">
                  Analyse de la game
                </div>
                <div className="space-y-1.5">
                  {insights.map((insight, i) => (
                    <InsightRow key={insight.id || i} insight={insight} />
                  ))}
                </div>
              </div>
            )}

            {/* v8.22 : Matchups dynamiques selon le rôle joué */}
            {(() => {
              // Définit les lignes selon le rôle joué (même logique que MatchupsSection)
              const rows = {
                ADC: [
                  { label: 'ADC',  mine: game.champion,  enemy: game.enemyAdc },
                  { label: 'SUP',  mine: game.support,   enemy: game.enemySupport },
                  { label: 'JG',   mine: game.jungler,   enemy: game.enemyJungler },
                ],
                MID: [
                  { label: 'MID',  mine: game.champion,  enemy: game.enemyMid },
                  { label: 'JG+',  mine: game.jungler,   enemy: null },
                  { label: 'JG-',  mine: null,           enemy: game.enemyJungler },
                ],
                TOP: [
                  { label: 'TOP',  mine: game.champion,  enemy: game.enemyTop },
                  { label: 'JG+',  mine: game.jungler,   enemy: null },
                  { label: 'JG-',  mine: null,           enemy: game.enemyJungler },
                ],
                JUNGLE: [
                  { label: 'JG',   mine: game.champion,  enemy: game.enemyJungler },
                  { label: 'SUP+', mine: game.support,   enemy: null },
                  { label: 'SUP-', mine: null,           enemy: game.enemySupport },
                ],
                SUPPORT: [
                  { label: 'SUP',  mine: game.champion,  enemy: game.enemySupport },
                  { label: 'ADC+', mine: game.allyAdc,   enemy: null },
                  { label: 'JG+',  mine: game.jungler,   enemy: null },
                ],
              };
              const lines = rows[playedRole] || rows.ADC;
              const hasData = lines.some(r => r.mine || r.enemy);
              if (!hasData) return null;
              return (
                <div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-2">Matchups</div>
                  <div className="space-y-1.5">
                    {lines.map(r => (
                      <MatchupRow key={r.label} label={r.label} mine={r.mine} enemy={r.enemy} version={version} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* V7.2 : Build final */}
            <ItemsRow items={game.items} version={version} />

            {/* V7.2 : Runes */}
            <RunesRow runes={game.runes} version={version} />

            <LaneStatsAt15 game={game} role={playedRole} />
            <DeathBreakdown deathDetails={game.deathDetails} />

            {game.notes && (
              <div className="text-[10px] text-zinc-400 italic border-l-2 border-zinc-800 pl-2.5 py-0.5 bg-zinc-900/50">
                "{game.notes}"
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={(e) => { e.stopPropagation(); onAnalyze?.(game); }}
                disabled={!onAnalyze}
                className="text-[10px] text-cyan-400 hover:text-cyan-300 font-bold tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ANALYSE DÉTAILLÉE →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// V7.7.1 — Helpers contributions + insights
// ============================================================

function ContributionRow({ contribution }) {
  const { status, label, reason, progress, target, objectiveType } = contribution;
  const isPositive = status === 'contributes';

  const Icon = isPositive ? CheckCircle2 : AlertTriangle;
  const colorClass = isPositive ? 'text-cyan-400' : 'text-rose-400';
  const tagLabel = objectiveType === 'daily_mission' ? 'Mission' : 'Bloc';

  return (
    <div className="flex items-start gap-2 text-[11px] py-1 px-2 bg-zinc-900/40 border-l-2 border-zinc-800 hover:border-zinc-700 transition">
      <Icon size={12} className={`${colorClass} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="text-[8px] uppercase tracking-wider text-zinc-500 font-bold">
            {tagLabel}
          </span>
          <span className="text-zinc-300 font-medium truncate">{label}</span>
          {typeof progress === 'number' && typeof target === 'number' && (
            <span className="text-[9px] font-mono text-zinc-500 flex-shrink-0">
              ({progress}/{target})
            </span>
          )}
        </div>
        <div className={`text-[10px] mt-0.5 ${colorClass}`}>{reason}</div>
      </div>
    </div>
  );
}

function InsightRow({ insight }) {
  const { severity, title, short, long } = insight;
  const [expanded, setExpanded] = useState(false);

  let colorClass, Icon, bgClass;
  switch (severity) {
    case 'critical':
      colorClass = 'text-rose-400';
      bgClass = 'bg-rose-500/5 border-rose-500/20';
      Icon = AlertTriangle;
      break;
    case 'warning':
      colorClass = 'text-amber-400';
      bgClass = 'bg-amber-500/5 border-amber-500/20';
      Icon = AlertTriangle;
      break;
    case 'positive':
      colorClass = 'text-emerald-400';
      bgClass = 'bg-emerald-500/5 border-emerald-500/20';
      Icon = Sparkles;
      break;
    case 'info':
    default:
      colorClass = 'text-cyan-400';
      bgClass = 'bg-cyan-500/5 border-cyan-500/20';
      Icon = Target;
      break;
  }

  return (
    <div
      className={`text-[11px] py-1.5 px-2 ${bgClass} border-l-2 cursor-pointer hover:bg-zinc-900/50 transition`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start gap-2">
        <Icon size={12} className={`${colorClass} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className={`font-bold ${colorClass}`}>{title}</span>
            <span className="text-zinc-400 text-[10px]">— {short}</span>
          </div>
          {expanded && long && (
            <div className="text-[10px] text-zinc-400 mt-1 italic">{long}</div>
          )}
        </div>
      </div>
    </div>
  );
}

